import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useRoomStore } from '@/stores/roomStore';
import { useMatchStore } from '@/stores/matchStore';
import { useNet } from '@/net/useNet';
import { getSocket } from '@/net/socket';
import { audio } from '@/audio/AudioService';
import { roomApi } from '@/rooms/roomApi';
import { socialApi, type FriendEntry } from '@/social/socialApi';
import { copyToClipboard } from '@/util/clipboard';
import { Avatar } from '@/art';
import { useT } from '@/i18n/useI18n';
import type { I18nKey } from '@/i18n/translations';
import type {
  RoomChatMessage,
  RoomDto,
  RoomMode,
  RoomSlotDto,
} from '@shared/protocol';
import type { TeamMode } from '@shared/types';

const DIVISION_ROMAN = ['', 'I', 'II', 'III', 'IV'] as const;

type RoomChatMsg = RoomChatMessage;

// Translation keys + color/disabled config per mode. Title + desc resolved
// at render time so language switches propagate without component remount.
const MODE_META: Record<RoomMode, { titleKey: I18nKey; descKey: I18nKey; color: string; disabled: boolean }> = {
  empty: { titleKey: 'room.mode.quick', descKey: 'room.mode.empty_desc', color: 'var(--ink-dim)', disabled: true },
  quick: { titleKey: 'room.mode.quick', descKey: 'room.mode.quick_desc', color: 'var(--c-cyan)', disabled: false },
  'solo-duel': { titleKey: 'room.mode.solo_duel', descKey: 'room.mode.solo_duel_desc', color: 'var(--c-yellow)', disabled: false },
  'duo-rank': { titleKey: 'room.mode.duo_rank', descKey: 'room.mode.duo_rank_desc', color: 'var(--c-purple)', disabled: false },
  'team-duel': { titleKey: 'room.mode.team_duel', descKey: 'room.mode.team_duel_desc', color: 'var(--c-red)', disabled: false },
  imbalanced: { titleKey: 'room.mode.imbalanced', descKey: 'room.mode.imbalanced_desc', color: 'var(--c-red)', disabled: true },
};

// 2v2 sub-modes — host picks one before starting a team-duel / duo-rank.
const TEAM_MODE_META: { key: TeamMode; titleKey: I18nKey; descKey: I18nKey; color: string }[] = [
  { key: 'alternating', titleKey: 'room.tmode.alternating', descKey: 'room.tmode.alternating_desc', color: 'var(--c-cyan)' },
  { key: 'split-color', titleKey: 'room.tmode.split_color', descKey: 'room.tmode.split_color_desc', color: 'var(--c-yellow)' },
  { key: 'attack-defense', titleKey: 'room.tmode.attack_defense', descKey: 'room.tmode.attack_defense_desc', color: 'var(--c-purple)' },
];

// Modes that need a 2v2 sub-mode selection.
function isTeamMode(m: RoomMode): boolean {
  return m === 'team-duel' || m === 'duo-rank';
}

export function RoomScreen() {
  const go = useUIStore((s) => s.go);
  const profile = useUserStore((s) => s.profile);
  const room = useRoomStore((s) => s.room);
  const setRoom = useRoomStore((s) => s.setRoom);
  // Keep the socket alive — RoomNotifier listens at App level so we only need
  // the connection here, not any handler registration.
  useNet({ autoConnect: true });

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharedChat, setSharedChat] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [messages, setMessages] = useState<RoomChatMsg[]>([]);
  const [chatText, setChatText] = useState('');
  const [chatExpanded, setChatExpanded] = useState(false);
  const setMatchSource = useMatchStore((s) => s.setMatchSource);
  const beginTeamPending = useMatchStore((s) => s.beginTeamPending);
  const t = useT();
  const isHost = room?.hostId === profile.id;
  const mySlot = room?.slots.find((s) => s.user?.id === profile.id) ?? null;
  const ready = mySlot?.ready ?? false;

  // RoomNotifier (App-level) keeps the store synced from socket events so we
  // don't subscribe again here. We DO refetch the snapshot on mount because
  // the store may be stale from a prior match (status='active') and the
  // `room:update` reset event might have fired while we were on a different
  // screen — the only guaranteed-fresh source is /rooms/mine.
  useEffect(() => {
    let cancel = false;
    roomApi.mine().then((r) => {
      if (cancel) return;
      if (r.room) setRoom(r.room);
      else { setRoom(null); go('home'); }
    }).catch(() => {
      if (!cancel) go('home');
    });
    return () => { cancel = true; };
  }, []);

  // Subscribe to room chat for the whole lifetime of the screen. The panel is
  // always visible inline, so messages just append. (Auto-scroll-to-bottom is
  // handled inside RoomChatLog.)
  useEffect(() => {
    const sock = getSocket();
    const onMsg = (m: RoomChatMsg) => setMessages((prev) => [...prev.slice(-49), m]);
    sock.on('room:chat', onMsg);
    return () => { sock.off('room:chat', onMsg); };
  }, []);

  if (!room) {
    return (
      <div className="screen screen-fit screen-fit--dense" style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="mono" style={{ color: 'var(--ink-dim)' }}>{t('common.loading')}</div>
      </div>
    );
  }

  const modeMeta = MODE_META[room.mode];
  const mode = { ...modeMeta, title: t(modeMeta.titleKey), desc: t(modeMeta.descKey) };
  const sideA = room.slots.filter((s) => s.side === 'A');
  const sideB = room.slots.filter((s) => s.side === 'B');
  const lastMsg = messages[messages.length - 1];

  async function handleLeave() {
    setBusy(true);
    try { await roomApi.leave(room!.code); } catch { /* noop */ }
    finally { setBusy(false); }
    setRoom(null);
    // Clear the room's pending-2v2 flag (+ any team state) so a following Quick
    // Match starts a normal 1v1 instead of resuming a stale 2v2 search.
    useMatchStore.getState().reset();
    go('home');
  }

  async function handleReady() {
    setBusy(true);
    try {
      const next = !ready;
      const r = await roomApi.setReady(room!.code, next);
      setRoom(r.room);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleStart() {
    setError(null);
    setBusy(true);
    try {
      const r = await roomApi.start(room!.code);
      // Route directly off the HTTP response instead of waiting for the
      // room:matchStart socket event. In quick mode the server deletes the
      // room, so relying on the broadcast was racy; the host navigates here.
      if (r.mode === 'quick') {
        setMatchSource('queue');
        go('matching');
      } else if (r.mode === 'solo-duel') {
        setMatchSource('room');
        go('matching');
      } else if (r.mode === 'team-duel' || r.mode === 'duo-rank') {
        // Both flows await TEAM_MATCH_FOUND on the matching screen. team-duel
        // spawns the 4-player match directly; duo-rank enqueues this duo and
        // pairs against another duo that picked the same sub-mode.
        beginTeamPending(r.teamMode ?? room!.teamMode);
        setMatchSource('room');
        go('matching');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleSetTeamMode(tm: TeamMode) {
    if (tm === room!.teamMode) return;
    setError(null);
    try {
      const r = await roomApi.setTeamMode(room!.code, tm);
      setRoom(r.room);
    } catch (e: any) { setError(e.message); }
  }

  async function handleJoinSlot(slot: 1 | 2 | 3 | 4) {
    setError(null);
    try {
      if (mySlot) {
        const r = await roomApi.move(room!.code, slot);
        setRoom(r.room);
      } else {
        const r = await roomApi.join(room!.code, slot);
        setRoom(r.room);
      }
    } catch (e: any) { setError(e.message); }
  }

  async function handleKick(userId: string) {
    try {
      const r = await roomApi.kick(room!.code, userId);
      setRoom(r.room);
    } catch (e: any) { setError(e.message); }
  }

  async function copyCode() {
    const ok = await copyToClipboard(room!.code);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }
  }

  function shareToChat() {
    audio.play('ui.message_send');
    getSocket().emit('chat:global', { text: t('chat.share_room_msg', { code: room!.code }) });
    setSharedChat(true);
    window.setTimeout(() => setSharedChat(false), 1600);
  }

  function sendRoomChat() {
    const body = chatText.trim();
    if (!body) return;
    audio.play('ui.message_send');
    getSocket().emit('room:chat', { text: body });
    setChatText('');
  }

  return (
    <div className="screen screen-fit screen-fit--dense screen-fit--room" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="hex-bg" />

      {/* Header */}
      <div style={{ padding: '16px 18px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button className="btn btn-ghost" style={{ fontSize: 11, padding: '8px 12px' }} onClick={handleLeave} disabled={busy}>
          {t('room.leave')}
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'var(--ink-dim)', letterSpacing: '0.16em' }}>{t('room.code_label')}</div>
          <div
            onClick={copyCode}
            className="display"
            style={{
              fontSize: 24,
              letterSpacing: '0.18em',
              color: 'var(--c-yellow)',
              cursor: 'pointer',
              textShadow: '0 0 12px rgba(244,199,36,0.6)',
            }}
          >
            {room.code}
          </div>
          <div style={{ fontSize: 9, color: copied ? 'var(--c-cyan)' : 'var(--ink-muted)' }}>
            {copied ? t('room.copied') : t('room.tap_to_copy')}
          </div>
        </div>
        <button
          className="btn"
          onClick={() => { useUIStore.getState().setReturnTo('room'); go('shop'); }}
          style={{
            fontSize: 11,
            padding: '8px 12px',
            background: 'rgba(244,199,36,0.12)',
            border: '1px solid var(--c-yellow)',
            color: 'var(--c-yellow)',
            whiteSpace: 'nowrap',
          }}
        >
          ⚡ {t('tab.shop')}
        </button>
      </div>

      {/* Mode banner */}
      <div style={{ padding: '0 18px 12px' }}>
        <div
          className="card"
          style={{
            padding: 12,
            borderColor: `${mode.color}40`,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              width: 6,
              alignSelf: 'stretch',
              background: mode.color,
              borderRadius: 3,
              boxShadow: `0 0 8px ${mode.color}`,
            }}
          />
          <div style={{ flex: 1 }}>
            <div className="display" style={{ fontSize: 13, color: mode.color, letterSpacing: '0.1em' }}>
              {mode.title}
            </div>
            <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 2 }}>{mode.desc}</div>
          </div>
        </div>
      </div>

      {/* Body — player slots + team-mode + a COMPACT chat. Everything stays
          FIXED (no body scroll): slots + team-mode picker keep their position
          and the chat is a single line + always-visible composer. Tap ⤢ to
          read full history in the expanded panel. */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* 2v2 sub-mode picker (team-duel / duo-rank only) */}
        {isTeamMode(room.mode) && (
          <div className="room-team-mode" style={{ padding: '4px 18px 8px' }}>
            <div className="display" style={{ fontSize: 10, color: 'var(--ink-muted)', letterSpacing: '0.14em', marginBottom: 6 }}>
              {t('room.team_mode_label')}
            </div>
            <div className="room-team-mode__options" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {TEAM_MODE_META.map((tm) => {
                const active = room.teamMode === tm.key;
                return (
                  <button
                    key={tm.key}
                    onClick={() => isHost && handleSetTeamMode(tm.key)}
                    disabled={!isHost}
                    className="card room-team-mode__option"
                    style={{
                      padding: 10,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: isHost ? 'pointer' : 'default',
                      border: `1px solid ${active ? tm.color : 'var(--hairline)'}`,
                      boxShadow: active ? `0 0 10px ${tm.color}40` : 'none',
                      background: active ? `${tm.color}14` : 'transparent',
                      opacity: !isHost && !active ? 0.5 : 1,
                    }}
                  >
                    <div className="room-team-mode__accent" style={{ width: 5, alignSelf: 'stretch', background: active ? tm.color : 'var(--hairline)', borderRadius: 3 }} />
                    <div className="room-team-mode__content" style={{ flex: 1 }}>
                      <div className="display room-team-mode__title" style={{ fontSize: 11, color: active ? tm.color : 'var(--ink)', letterSpacing: '0.08em' }}>
                        {t(tm.titleKey)}
                      </div>
                      <div className="room-team-mode__desc" style={{ fontSize: 9, color: 'var(--ink-muted)', marginTop: 2 }}>{t(tm.descKey)}</div>
                    </div>
                    {active && <div className="display" style={{ fontSize: 10, color: tm.color }}>✓</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Team A */}
        <TeamRow label={t('room.team_a')} color="var(--c-cyan)" slots={sideA} meId={profile.id} hostId={room.hostId} isHost={isHost} onJoin={handleJoinSlot} onKick={handleKick} />

        {/* VS divider */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0', position: 'relative' }}>
          <div style={{
            height: 1, background: 'linear-gradient(90deg, transparent, var(--hairline), transparent)',
            position: 'absolute', left: 18, right: 18, top: '50%',
          }} />
          <div className="display" style={{
            fontSize: 16, padding: '2px 14px',
            background: 'var(--bg-deep)', color: 'var(--c-red)',
            letterSpacing: '0.2em', position: 'relative',
          }}>VS</div>
        </div>

        {/* Team B */}
        <TeamRow label={t('room.team_b')} color="var(--c-red)" slots={sideB} meId={profile.id} hostId={room.hostId} isHost={isHost} onJoin={handleJoinSlot} onKick={handleKick} />

        {/* Error toast */}
        {error && (
          <div style={{ padding: '8px 18px' }}>
            <div className="card" style={{ padding: 10, borderColor: 'var(--c-red)40', color: 'var(--c-red)', fontSize: 11 }}>
              {error}
            </div>
          </div>
        )}
        {/* Compact team chat — one-line preview + an always-visible composer.
            marginTop:auto pins it to the bottom of the body so it stays put as
            the slot area changes height (1v1 vs 2v2). Tap ⤢ for full history. */}
        <div className="room-screen__chat" style={{ padding: '4px 18px 8px', marginTop: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="display" style={{ fontSize: 10, color: 'var(--c-cyan)', letterSpacing: '0.14em' }}>
              {t('room.chat_title')}
            </div>
            <button
              onClick={() => setChatExpanded(true)}
              title={t('room.chat_expand')}
              style={{
                background: 'transparent', border: '1px solid var(--hairline)', borderRadius: 6,
                color: 'var(--c-cyan)', width: 26, height: 22, cursor: 'pointer', fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              }}
            >
              ⤢
            </button>
          </div>
          <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* One-line preview of the latest message; tap to expand for more. */}
            <div
              onClick={() => setChatExpanded(true)}
              style={{
                padding: '8px 10px', fontSize: 11, lineHeight: 1.3, cursor: 'pointer',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                color: lastMsg ? 'var(--ink)' : 'var(--ink-dim)',
              }}
            >
              {lastMsg ? (
                <>
                  <span style={{ color: 'var(--c-cyan)', marginRight: 4 }}>
                    {lastMsg.sender.id === profile.id ? t('room.you_suffix') : lastMsg.sender.displayName}:
                  </span>
                  {lastMsg.text}
                </>
              ) : t('room.chat_empty')}
            </div>
            <RoomChatComposer value={chatText} onChange={setChatText} onSend={sendRoomChat} />
          </div>
        </div>
      </div>

      {/* Footer actions — always pinned bottom */}
      <div className="room-screen__footer" style={{ padding: '10px 18px 18px', display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--hairline)', background: 'rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn"
            onClick={() => setInviteOpen(true)}
            style={{
              flex: 1,
              fontSize: 11,
              padding: '8px',
              background: 'rgba(155,93,229,0.15)',
              border: '1px solid var(--c-purple)',
              color: 'var(--c-purple)',
            }}
          >
            {t('room.invite_friend')}
          </button>
          <button
            className="btn"
            onClick={shareToChat}
            disabled={sharedChat}
            style={{
              flex: 1,
              fontSize: 11,
              padding: '8px',
              background: sharedChat ? 'rgba(34,211,238,0.18)' : 'rgba(34,211,238,0.12)',
              border: '1px solid var(--c-cyan)',
              color: 'var(--c-cyan)',
            }}
          >
            {sharedChat ? t('room.shared_to_chat') : t('room.share_to_chat')}
          </button>
        </div>
        {mySlot && (
          <button
            className="btn"
            style={{
              fontSize: 13,
              padding: '11px',
              background: ready ? 'var(--ink-dim)' : 'rgba(34,211,238,0.18)',
              border: `1px solid ${ready ? 'var(--ink-muted)' : 'var(--c-cyan)'}`,
              color: ready ? 'var(--ink)' : 'var(--c-cyan)',
            }}
            onClick={handleReady}
            disabled={busy}
          >
            {ready ? `✓ ${t('room.ready_label')}` : t('room.tap_to_ready')}
          </button>
        )}
        {isHost && (
          <button
            className="btn btn-primary"
            style={{ fontSize: 16, padding: '13px' }}
            onClick={handleStart}
            disabled={busy || mode.disabled || (room.mode !== 'quick' && !allOthersReady(room, profile.id))}
          >
            {room.mode === 'quick' ? t('room.start_quick') : room.mode === 'solo-duel' ? t('room.start_duel') : isTeamMode(room.mode) ? t('room.start_team') : t('room.start')}
          </button>
        )}
        {!isHost && (
          <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--ink-dim)' }}>
            {t('room.waiting_host')}
          </div>
        )}
      </div>


      {inviteOpen && (
        <InviteFriendModal
          code={room.code}
          existingUserIds={room.slots.map((s) => s.user?.id).filter((x): x is string => !!x)}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {chatExpanded && (
        <RoomChatModal
          messages={messages}
          meId={profile.id}
          value={chatText}
          onChange={setChatText}
          onSend={sendRoomChat}
          onClose={() => setChatExpanded(false)}
        />
      )}
    </div>
  );
}

// Self-scrolling message log, shared by the compact inline chat and the
// expanded modal. Pins to the newest line whenever the list grows.
function RoomChatLog({ messages, meId, style }: { messages: RoomChatMsg[]; meId: string; style?: CSSProperties }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);
  return (
    <div
      ref={ref}
      style={{ overflowY: 'auto', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, ...style }}
    >
      {messages.length === 0 && (
        <div style={{ margin: 'auto', textAlign: 'center', fontSize: 10, color: 'var(--ink-dim)', padding: '8px 0' }}>
          {t('room.chat_empty')}
        </div>
      )}
      {messages.map((m) => {
        const mine = m.sender.id === meId;
        return (
          <div key={m.id} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 6, alignItems: 'flex-end' }}>
            {!mine && <Avatar color={m.sender.avatarColor} size={22} photoUrl={m.sender.avatarUrl ?? undefined} />}
            <div style={{ maxWidth: '74%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              {!mine && (
                <div style={{ fontSize: 8, color: 'var(--ink-muted)', marginBottom: 1, paddingLeft: 2 }}>{m.sender.displayName}</div>
              )}
              <div
                style={{
                  padding: '5px 9px', borderRadius: 10, fontSize: 11, lineHeight: 1.3, wordBreak: 'break-word',
                  background: mine ? 'rgba(34,211,238,0.18)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${mine ? 'var(--c-cyan)' : 'var(--hairline)'}`,
                  color: 'var(--ink)',
                  borderBottomRightRadius: mine ? 2 : 10,
                  borderBottomLeftRadius: mine ? 10 : 2,
                }}
              >
                {m.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RoomChatComposer({ value, onChange, onSend, autoFocus }: { value: string; onChange: (v: string) => void; onSend: () => void; autoFocus?: boolean }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: '1px solid var(--hairline)' }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSend(); } }}
        maxLength={280}
        autoFocus={autoFocus}
        placeholder={t('room.chat_placeholder')}
        style={{
          flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid var(--hairline)', borderRadius: 8,
          padding: '8px 10px', color: 'var(--ink)', fontSize: 12, outline: 'none',
        }}
      />
      <button
        onClick={onSend}
        disabled={!value.trim()}
        className="btn"
        style={{
          padding: '0 14px', fontSize: 10, borderRadius: 8,
          background: value.trim() ? 'rgba(34,211,238,0.2)' : 'var(--ink-dim)',
          border: `1px solid ${value.trim() ? 'var(--c-cyan)' : 'var(--ink-muted)'}`,
          color: value.trim() ? 'var(--c-cyan)' : 'var(--ink-muted)',
        }}
      >
        {t('room.chat_send')}
      </button>
    </div>
  );
}

interface RoomChatModalProps {
  messages: RoomChatMsg[];
  meId: string;
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onClose: () => void;
}

function RoomChatModal({ messages, meId, value, onChange, onSend, onClose }: RoomChatModalProps) {
  const t = useT();
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
        zIndex: 360, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: '100%', maxWidth: 480, margin: '0 auto', height: '70vh',
          display: 'flex', flexDirection: 'column',
          background: 'rgba(15,18,32,0.98)',
          borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottom: 'none', overflow: 'hidden',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="display" style={{ fontSize: 13, color: 'var(--c-cyan)', letterSpacing: '0.12em' }}>
            {t('room.chat_title')}
          </div>
          <button onClick={onClose} title={t('room.chat_collapse')} style={{ background: 'transparent', border: '1px solid var(--hairline)', color: 'var(--ink-muted)', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>×</button>
        </div>
        <RoomChatLog messages={messages} meId={meId} style={{ flex: 1, minHeight: 0 }} />
        <RoomChatComposer value={value} onChange={onChange} onSend={onSend} autoFocus />
      </div>
    </div>
  );
}

interface InviteFriendModalProps {
  code: string;
  existingUserIds: string[];
  onClose: () => void;
}

function InviteFriendModal({ code, existingUserIds, onClose }: InviteFriendModalProps) {
  const t = useT();
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<string | null>(null);
  const inRoom = new Set(existingUserIds);

  useEffect(() => {
    socialApi.list().then((d) => {
      setFriends([...d.friends].sort((a, b) => (b.online ? 1 : 0) - (a.online ? 1 : 0)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function handleInvite(userId: string) {
    setSending(userId);
    try {
      await roomApi.invite(code, userId);
      setSent((s) => new Set(s).add(userId));
    } catch { /* noop */ }
    setSending(null);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        zIndex: 350,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: '100%',
          maxWidth: 320,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(15,18,32,0.97)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="display" style={{ fontSize: 13, color: 'var(--c-purple)', letterSpacing: '0.1em' }}>
            {t('room.invite_modal_title')}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--hairline)', color: 'var(--ink-muted)', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 13 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: 'var(--ink-dim)' }}>{t('common.loading')}</div>}
          {!loading && friends.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: 'var(--ink-dim)' }}>
              {t('room.no_friends_to_invite')}
            </div>
          )}
          {friends.map((f) => {
            const isIn = inRoom.has(f.userId);
            const wasSent = sent.has(f.userId);
            return (
              <div
                key={f.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 14px',
                  borderBottom: '1px solid var(--hairline)',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Avatar color={f.avatarColor} size={32} photoUrl={f.avatarUrl ?? undefined} characterUrl={f.characterIdleUrl} frame={f.frame} />
                  <span
                    style={{
                      position: 'absolute', bottom: -1, right: -1, width: 10, height: 10,
                      borderRadius: 5,
                      background: f.online ? 'var(--c-cyan)' : 'var(--ink-dim)',
                      border: '2px solid var(--bg-deep)',
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--f-display)', fontSize: 12 }}>{f.displayName}</div>
                  <div style={{ fontSize: 9, color: f.online ? 'var(--c-cyan)' : 'var(--ink-dim)' }}>
                    {f.online ? t('common.online') : t('common.offline')}
                  </div>
                </div>
                {isIn ? (
                  <span style={{ fontSize: 9, color: 'var(--ink-dim)', fontFamily: 'var(--f-display)' }}>{t('room.in_room')}</span>
                ) : wasSent ? (
                  <span style={{ fontSize: 9, color: 'var(--c-cyan)', fontFamily: 'var(--f-display)' }}>{t('room.invited')}</span>
                ) : (
                  <button
                    disabled={sending === f.userId || !f.online}
                    onClick={() => handleInvite(f.userId)}
                    style={{
                      background: f.online ? 'var(--c-purple)' : 'var(--ink-dim)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 10px',
                      fontFamily: 'var(--f-display)',
                      fontSize: 10,
                      letterSpacing: '0.06em',
                      cursor: f.online ? 'pointer' : 'not-allowed',
                    }}
                    title={f.online ? t('room.send_invite') : t('room.offline_send_hint')}
                  >
                    {t('room.invite_btn')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function allOthersReady(room: RoomDto, hostId: string): boolean {
  return room.slots
    .filter((s) => s.user && s.user.id !== hostId)
    .every((s) => s.ready);
}

interface TeamRowProps {
  label: string;
  color: string;
  slots: RoomSlotDto[];
  meId: string;
  hostId: string;
  isHost: boolean;
  onJoin: (slot: 1 | 2 | 3 | 4) => void;
  onKick: (userId: string) => void;
}

function TeamRow({ label, color, slots, meId, hostId, isHost, onJoin, onKick }: TeamRowProps) {
  return (
    <div style={{ padding: '4px 18px 6px' }}>
      <div className="display" style={{ fontSize: 10, color, letterSpacing: '0.14em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {slots.map((s) => (
          <SlotCard
            key={s.slot}
            slot={s}
            color={color}
            meId={meId}
            hostId={hostId}
            isHost={isHost}
            onJoin={() => onJoin(s.slot)}
            onKick={() => s.user && onKick(s.user.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface SlotCardProps {
  slot: RoomSlotDto;
  color: string;
  meId: string;
  hostId: string;
  isHost: boolean;
  onJoin: () => void;
  onKick: () => void;
}

function SlotCard({ slot, color, meId, hostId, isHost, onJoin, onKick }: SlotCardProps) {
  const t = useT();
  const u = slot.user;
  const isMe = u?.id === meId;
  const isHostSlot = u?.id === hostId;
  if (!u) {
    return (
      <button
        onClick={onJoin}
        className="card"
        style={{
          padding: 10,
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px dashed ${color}60`,
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--ink-dim)' }}>
          <div style={{ fontSize: 22 }}>+</div>
          <div style={{ fontSize: 9, letterSpacing: '0.1em' }}>{t('room.join_slot')}</div>
        </div>
      </button>
    );
  }
  return (
    <div
      className="card"
      style={{
        padding: 8,
        minHeight: 64,
        border: `1px solid ${slot.ready ? color : 'var(--hairline)'}`,
        boxShadow: slot.ready ? `0 0 10px ${color}40` : 'none',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Avatar color={u.avatarColor} size={34} photoUrl={u.avatarUrl ?? undefined} characterUrl={u.characterIdleUrl} frame={u.frame} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--f-display)',
            fontSize: 11,
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {u.displayName}
          {isHostSlot && <span style={{ color: 'var(--c-yellow)', marginLeft: 4 }}>♛</span>}
          {isMe && <span style={{ color: 'var(--c-cyan)', marginLeft: 4, fontSize: 8 }}>{t('room.you_suffix')}</span>}
        </div>
        <div style={{ fontSize: 9, color: 'var(--ink-muted)', marginTop: 2 }}>
          {u.rank.toUpperCase()} {DIVISION_ROMAN[u.rankDivision] ?? ''} · {u.mmr} MMR
        </div>
      </div>
      {slot.ready && (
        <div className="display" style={{ fontSize: 9, color, letterSpacing: '0.1em' }}>{t('room.ready_label')}</div>
      )}
      {isHost && !isMe && (
        <button
          onClick={(e) => { e.stopPropagation(); onKick(); }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'transparent',
            border: 'none',
            color: 'var(--c-red)',
            fontSize: 14,
            cursor: 'pointer',
            padding: '2px 6px',
          }}
          title={t('room.kick_tip')}
        >
          ×
        </button>
      )}
    </div>
  );
}
